import assert from 'assert';
import React from 'react';
import secondsToMilliseconds from 'date-fns/secondsToMilliseconds';
import throttle from 'lodash-es/throttle';
import ms from 'ms';
import type { Address } from 'viem';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { SUPER_MINTER_ADDRESS } from '@soundxyz/tiered-sdk-viem/abi/super-minter';
import { assertAddress } from '@soundxyz/utils/address';
import { ContractMethod, NULL_ADDRESS } from '@soundxyz/utils/const';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useMintReferral } from '@context/mintReferral';
import { ModalContext } from '@context/modal';
import { ToastContext } from '@context/toast';
import { Transaction } from '@context/transaction';
import { getTransactionResponse } from '@context/transaction/TransactionStatusStore';
import { type FragmentType, getFragment, TieredBuyButtonsFragmentDoc } from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { useSubscribeReleaseCollector } from '@hooks/useSubscribeRelease';
import { useTieredSdk } from '@hooks/useTieredSdk';
import { trackClickEvent } from '@lib/analytics';
import {
  type SuperMinterScheduleWithTier,
  useEstimateTieredMintGas,
  useTieredEditionEligibleQuantity,
  useTieredMintPrice,
} from '@store/sdk/tieredReactQuery';
import { chainFromId } from '@utils/chain';
import { humanReadableChainName } from '@utils/helpers';
import { getMerkleProof } from '@utils/merkle';
import { PayTieredEditionWithCreditCard } from '../CreditCard/TieredPayWithCCButton';
import { PayWithETHButton } from '../L2CheckoutWithEth/PayWithEthButton';
import { PayTierEditionWithEth } from '../L2CheckoutWithEth/TieredCheckoutWithEth';
import { sendReferralSourceIfPresent } from '../ReferralSource';

type CollectStep = 'active-mint-schedules' | 'eligible-quantity' | 'mint' | 'handle-transaction';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment TieredBuyButtons on Release {
    id
    contractAddress
    chainId
    ...ReleaseReferrableInfo
  }
`);

export const TieredBuyButtons = ({
  releaseData,
  schedule,
  error,
  quantity,
  giftAddress,
  giftingOpened,
}: {
  releaseData: FragmentType<TieredBuyButtonsFragmentDoc>;
  schedule: SuperMinterScheduleWithTier;
  error: boolean;
  quantity: number;
  giftAddress: Address | null;
  giftingOpened?: boolean;
}) => {
  const { authUser, authUserAddress } = useAuth();
  const { provider, expectMatchingChain, activeChainId, getDeepLink } = useConnectedAuth();
  const { tieredSigner } = useTieredSdk();
  const [lastBlockTimestamp, setLastBlockTimestamp] = React.useState(0n);
  const logError = useLogError();
  const logInfo = useLogInfo();
  const { openToast } = ToastContext.useContainer();
  const { closeModalByType } = ModalContext.useContainer();
  const { handleTransaction } = Transaction.useContainer();

  const disabledCollectGifting = React.useMemo(
    () => !giftAddress && !!giftingOpened,
    [giftAddress, giftingOpened],
  );

  const release = getFragment(TieredBuyButtonsFragmentDoc, releaseData);
  const { id: releaseId, contractAddress, chainId } = release;

  const canCollectSafely = React.useMemo(() => {
    if (!authUser || !!error) {
      return false;
    }

    const currentSchedStartTime = schedule?.startTime;
    if (!currentSchedStartTime) {
      return false;
    }

    return lastBlockTimestamp > currentSchedStartTime - secondsToMilliseconds(12);
  }, [authUser, error, schedule?.startTime, lastBlockTimestamp]);

  const chainProvider = React.useMemo(() => provider(chainId), [chainId, provider]);
  React.useEffect(() => {
    if (canCollectSafely) return;

    /**
     * We need to do a lookup for the current/latest block of the chain which is going to be used for canCollectSafely check
     */
    chainProvider
      .getBlock({
        blockTag: 'latest',
      })
      .then(({ timestamp }) => {
        // handle race conditions and only updates if increasing
        setLastBlockTimestamp(existing => (timestamp > existing ? timestamp : existing));
      })
      .catch(error => {
        logError({
          action: MINTING_ERROR_ACTIONS.BLOCK_LISTENER_ERROR,
          error,
          indexedTags: { file: 'TieredBuyButtons.tsx', context: 'blockListener' },
          errorType: ERROR_TYPE.BLOCK_LISTENER,
          pillar: PILLARS.MINTING,
          level: 'warning',
          message: `Error in getting initial block`,
          isArtistOnlyPage: false,
        });
      });

    /**
     * Watch blocks only notifies when a new block is mined which could take some time
     * It returns the cleanup function so if canCollectSafely is flipped, it will be unsubscribed automatically
     */
    return chainProvider.watchBlocks({
      onBlock({ timestamp }) {
        // handle race conditions and only updates if increasing
        setLastBlockTimestamp(existing => (timestamp > existing ? timestamp : existing));
      },
    });
  }, [canCollectSafely, logError, chainProvider]);

  const [isBuying, setIsBuying] = React.useState(false);

  const { data: eligibleQuantity = null, refetch: refetchEligibleQuantity } =
    useTieredEditionEligibleQuantity({
      mintSchedule: schedule,
      chainId,
      editionAddress: contractAddress,
    });

  const throttledRefetchEligibleQuantity = React.useMemo(
    () =>
      throttle(refetchEligibleQuantity, ms('10 seconds'), {
        leading: true,
        trailing: true,
      }),
    [refetchEligibleQuantity],
  );

  useSubscribeReleaseCollector({
    releaseId,
    onData() {
      throttledRefetchEligibleQuantity();
    },
    enabled: true,
  });

  const { getAffiliate } = useCheckReferrableRelease({
    release,
  });

  const { referral } = useMintReferral();

  const affiliate = getAffiliate(referral) ?? NULL_ADDRESS;

  assertAddress(affiliate);

  const { totalMintPriceWei } = useTieredMintPrice({
    editionAddress: contractAddress,
    mintSchedule: schedule,
    chainId,
    quantity,
  });

  const { data: tieredMintGasLimit, isMintEstimationLoadingWithoutErrors } =
    useEstimateTieredMintGas({
      mintSchedule: schedule,
      chainId,
      editionAddress: contractAddress,
      quantity,
      totalValue: totalMintPriceWei?.total,
    });

  const handleBuy = useStableCallback(async () => {
    const chain = expectMatchingChain(chainId);

    if (chain.stop) return;

    setIsBuying(true);

    try {
      if (chain.switching) await chain.switching;
    } catch (error) {
      openToast({
        text: `Please switch to ${humanReadableChainName(chainId)}`,
        variant: 'error',
      });
      setIsBuying(false);
      return;
    }

    // expectMatchingChain should guard against ever hitting this, but just in case
    if (activeChainId.current !== chainId) {
      logError({
        action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
        error: new Error('Chain mismatch after switch'),
        indexedTags: { releaseId, contractAddress },
        errorType: ERROR_TYPE.SWITCHING_CHAINS_ERROR,
        pillar: PILLARS.MINTING,
        level: 'error',
        message: `Error in switching to chain ${chainId}`,
        toast: `Please switch to ${humanReadableChainName(chainId)} network on your wallet`,
        isArtistOnlyPage: false,
      });
      return;
    }

    let collectStep: CollectStep = 'active-mint-schedules';
    try {
      if (!schedule) {
        openToast({
          text: 'No active mint schedule available',
          variant: 'error',
        });
        return;
      }

      collectStep = 'eligible-quantity';

      // We disable collecting on 0 or nullish
      if (!eligibleQuantity) {
        openToast({
          text: schedule.minted >= schedule.maxMintable ? 'Sold out!' : 'Minting is not available',
          variant: 'error',
        });

        closeModalByType('BUY_NFT');

        return;
      }

      const allowGifting = !!giftAddress && giftAddress !== authUserAddress;
      assert(authUserAddress);
      const toAddress = !!allowGifting ? giftAddress : authUserAddress;

      trackClickEvent({
        event: 'Pay with ETH Clicked',
        properties: {
          associationId: releaseId,
          associationType: 'Release',
          quantity,
          chainId,
          affiliateAddress: affiliate.toLowerCase(),
          // TODO: FIX https://linear.app/sound/issue/ENG-8132/fix-analytics-types-on-new-schedules-properties
          minterAddress: SUPER_MINTER_ADDRESS,
          // In future we should pass in the source component for analytics
          sourceComponent: 'Collect Modal',
          mintTo: toAddress,
        },
      });

      /**
       * This is critical for a better mobile UX.
       * We have to set before the actual transaction is sent.
       * This way it can open the user's wallet app and make it a seamless flow
       */
      const deepLink = getDeepLink();
      if (deepLink) {
        window.location.href = deepLink;
      }

      const transactionFn = async () => {
        assert(totalMintPriceWei, 'Unexpected missing price');

        collectStep = 'mint';

        const wallet = await tieredSigner(chainId);

        assertAddress(contractAddress);

        const allowlistProof =
          schedule.mode === 'VERIFY_MERKLE'
            ? await getMerkleProof({
                merkleRoot: schedule.merkleRoot,
                userAddress: authUserAddress,
              })
            : null;

        if (schedule.mode === 'VERIFY_MERKLE') {
          if (!allowlistProof) throw Error('Not eligible!');
        }

        const transactionHash = await wallet.writeMintTieredEdition({
          affiliate,
          chain: chainFromId(chainId),
          edition: contractAddress,
          mintParams: allowlistProof
            ? {
                mode: 'VERIFY_MERKLE',
                allowlisted: authUserAddress,
                allowlistProof,
              }
            : {
                mode: 'DEFAULT',
              },
          quantity,
          scheduleNum: schedule.scheduleNum,
          tier: schedule.tier,
          to: toAddress,
          value: totalMintPriceWei.total,
          gasLimit: tieredMintGasLimit?.gasLimit ?? undefined,
        });

        sendReferralSourceIfPresent({
          txHash: transactionHash,
          chainId,
          referral: referral,
        });

        return getTransactionResponse({
          hash: transactionHash,
          chainId,
          provider: chainProvider,
        });
      };

      collectStep = 'handle-transaction';

      logInfo({
        action: MINTING_INFO_ACTIONS.EDITION_MINT,
        message: allowGifting ? 'Minting gifted edition' : 'Minting edition',
        data: {
          releaseId,
          quantity,
          chainId,
          giftAddress,
        },
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
      });
      const status = await handleTransaction({
        contractMethod: ContractMethod.ARTIST__BUY_EDITION,
        transactionFn,
        releaseId,
        chainId,
        openProcessingModal: true,
      });
      if (status) {
        trackClickEvent({
          event: 'Pay with ETH Success',
          properties: {
            associationId: releaseId,
            associationType: 'Release',
            quantity,
            chainId,
            affiliateAddress: affiliate.toLowerCase(),
            // TODO: FIX https://linear.app/sound/issue/ENG-8132/fix-analytics-types-on-new-schedules-properties
            minterAddress: SUPER_MINTER_ADDRESS,
            // In future we should pass in the source component for analytics
            sourceComponent: 'Collect Modal',
            mintTo: toAddress,
            isGifted: allowGifting,
          },
        });
      }
    } catch (error) {
      logError({
        action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
        error,
        toast: 'Error while collecting edition B',
        message: `Errored while collecting ${collectStep}`,
        errorType: ERROR_TYPE.EDITION_MINT,
        indexedTags: {
          releaseId,
          selectedQuantity: quantity,
          chainId,
        },
        isArtistOnlyPage: false,
        pillar: PILLARS.MINTING,
        level: 'fatal',
      });
    } finally {
      setIsBuying(false);
    }
  });

  const disabledCollecting =
    isBuying ||
    error ||
    // We disable collecting on 0 or nullish
    !eligibleQuantity ||
    // This means the transaction fee is loading, we need to wait for the expected value
    totalMintPriceWei == null ||
    isMintEstimationLoadingWithoutErrors;

  return (
    <div className="-mt-1 flex w-full flex-col gap-y-3">
      <PayWithETHButton
        onBuy={handleBuy}
        // Collecting is disabled when this value is not resolved yet anyways
        totalPriceWei={totalMintPriceWei?.total?.toString() ?? '0'}
        chainId={chainId}
        disableCollecting={disabledCollecting || disabledCollectGifting}
        isBuying={isBuying}
        payWithEthButton={
          <PayTierEditionWithEth
            source="Insufficient Funds Modal"
            editionAddress={contractAddress}
            releaseId={releaseId}
            activeSchedule={schedule}
            chainId={chainId}
            quantity={quantity}
            canCollectSafely={canCollectSafely}
            disabled={error || disabledCollectGifting}
            className="justify-between"
            variant="fuchsia"
            giftTo={giftAddress}
          />
        }
        payWithCardButton={
          <PayTieredEditionWithCreditCard
            releaseId={releaseId}
            editionAddress={contractAddress}
            activeSchedule={schedule}
            canCollectSafely={canCollectSafely}
            disabled={error || disabledCollecting || disabledCollectGifting}
            chainId={chainId}
            quantity={quantity}
            source="Insufficient Funds Modal"
            variant="tertiary"
            giftTo={giftAddress}
          />
        }
      />
      <PayTieredEditionWithCreditCard
        releaseId={releaseId}
        editionAddress={contractAddress}
        activeSchedule={schedule}
        canCollectSafely={canCollectSafely}
        disabled={error || disabledCollecting || disabledCollectGifting}
        chainId={chainId}
        quantity={quantity}
        source="Collect Modal"
        variant="tertiary"
        giftTo={giftAddress}
      />
    </div>
  );
};
